import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../../../../library/button/button';
import InputGroup from '../../../../library/input-group/input-group';
import FitmentTireCodeDescription from '../../../../components/fitment-tire-code-description/fitment-tire-code-description';
import Modal from '../../../../components/modal/modal';
import ProductSwatchSelectionOneDT from '../../../../components/product-swatch-selection/product-swatch-selection';
import CanonicalFormField from '../canonical-form-field/canonical-form-field';

import { useFitment } from '../../../../shared/hooks/hooks';
import useCanonicalDefaultProduct from '../../hooks/use-canonical-default-product/use-canonical-default-product';
import useCanonicalForm from '../../hooks/use-canonical-form/use-canonical-form';

import {
  getNormalizedProductType,
  isCanonicalProduct
} from '../../../../shared/utils/product/product';
import { getCompletedCanonicalFilterSelections } from '../../../../shared/utils/product-detail-utils/product-detail-utils';
import { ProductProp } from '../../../../shared/utils/prop-types/prop-types';
import { isArrayWithLength } from '../../../../shared/utils/validators/validators';

import { ACCESSORY_FILTER_KEYS as FILTER_KEYS } from '../../../../shared/constants/filters';
import { PRODUCT_TYPES } from '../../../../shared/constants/products';

import './canonical-accessory-form.scss';

const MODAL_TYPES = {
  INTERNAL_LENGTH: 'INTERNAL_LENGTH',
  LUG_TYPE: 'LUG_TYPE',
  SEAT_TYPE: 'SEAT_TYPE',
  THREAD_DIAMETER: 'THREAD_DIAMETER',
  THREAD_PITCH: 'THREAD_PITCH'
};
function CanonicalAccessoryForm(props) {
  const {
    isLoading,
    onFilterChange,
    onSelectSwatch,
    product,
    selectedSwatch,
    swatches
  } = props;

  const canonicalForm = useCanonicalForm(product, PRODUCT_TYPES.ACCESSORIES);
  const { shouldIgnoreDefaultProduct } = useCanonicalDefaultProduct(product);
  const { selectedFitment } = useFitment();
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const canonicalFormRef = useRef(canonicalForm);

  const isCanonical = isCanonicalProduct(product);
  const productType = getNormalizedProductType(product);
  const hasSwatches = Boolean(product?.swatches);
  const hasSelectedFitment = Boolean(selectedFitment);
  const { filters, onSelectFilter, queryFilters, selectedFilters } =
    canonicalForm;

  canonicalFormRef.current = canonicalForm;

  function toggleHelp() {
    setIsHelpOpen(!isHelpOpen);
  }

  function openModal(type) {
    setModalType(type);
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
  }

  function getOptions(name) {
    let options = null;

    if (filters[name]) {
      options = filters[name];
    } else if (selectedFilters[name]) {
      options = [selectedFilters[name]];
    }

    return options;
  }

  function getValue(name) {
    let value = null;
    if (selectedFilters[name]) {
      value = selectedFilters[name];
    } else if (!isCanonical && productType === PRODUCT_TYPES.ACCESSORIES) {
      let key = name;

      if (name === FILTER_KEYS.CHAIN_DIAMETER) {
        key = FILTER_KEYS.CHAIN_DIAMETER;
      } else if (name === FILTER_KEYS.CHAIN_ASPECT_RATIO) {
        key = FILTER_KEYS.CHAIN_ASPECT_RATIO;
      }
      const productVal = product[key];

      if (productVal) {
        value = { label: productVal, value: productVal };
      }
    }
    return value;
  }

  function handleSelection(key, value) {
    if (typeof onSelectFilter === 'function' && key && value) {
      onSelectFilter(
        { [key]: { ...value, wasAutoSelected: false } },
        value.shouldResetFilter
      );
    }
  }

  function onSwatchChange(swatch) {
    if (swatch?.altText) {
      onSelectFilter(
        {
          color: {
            value: swatch.altText
          }
        },
        true
      );
      onSelectSwatch?.(swatch);
    }
  }

  useEffect(() => {
    const isCanonical = isCanonicalProduct(product);
    const productType = getNormalizedProductType(product);
    const { onComplete, onSelectFilter, selectedFilters } =
      canonicalFormRef.current;

    const hasDefaultProduct = Boolean(product?.aggregates?.defaultProduct);
    const hasFilteredBySwatchIfAvailable =
      !product?.aggregates?.nextFilters?.some(f => f.key === FILTER_KEYS.COLOR);

    const shouldUseDefaultProduct =
      !isLoading &&
      !shouldIgnoreDefaultProduct &&
      isCanonical &&
      hasDefaultProduct &&
      hasSelectedFitment &&
      hasFilteredBySwatchIfAvailable;

    if (
      (!isCanonical || shouldUseDefaultProduct) &&
      productType === PRODUCT_TYPES.ACCESSORIES
    ) {
      const srcProduct = shouldUseDefaultProduct
        ? product.aggregates.defaultProduct
        : product;

      const {
        colorName,
        internalLength,
        lugType,
        seatType,
        threadDiameter,
        threadPitch
      } = srcProduct || {};

      let chainSectionWidth, chainDiameter, chainAspectRatio;

      product?.aggregates?.nextFilters?.map(v => {
        if (v.key === FILTER_KEYS.CHAIN_SECTION_WIDTH) {
          chainSectionWidth = v.options?.[0]?.toString();
        } else if (v.key === FILTER_KEYS.CHAIN_DIAMETER) {
          chainDiameter = v.options?.[0]?.toString();
        } else if (v.key === FILTER_KEYS.CHAIN_ASPECT_RATIO) {
          chainAspectRatio = v.options?.[0]?.toString();
        }
      });

      const completedFilters = getCompletedCanonicalFilterSelections(
        [
          { key: FILTER_KEYS.CHAIN_SECTION_WIDTH, value: chainSectionWidth },
          { key: FILTER_KEYS.CHAIN_ASPECT_RATIO, value: chainAspectRatio },
          { key: FILTER_KEYS.CHAIN_DIAMETER, value: chainDiameter },
          { key: FILTER_KEYS.COLOR, value: colorName },
          { key: FILTER_KEYS.INTERNAL_LENGTH, value: internalLength },
          { key: FILTER_KEYS.LUG_TYPE, value: lugType },
          { key: FILTER_KEYS.THREAD_DIAMETER, value: threadDiameter },
          { key: FILTER_KEYS.THREAD_PITCH, value: threadPitch },
          { key: FILTER_KEYS.SEAT_TYPE, value: seatType }
        ],
        selectedFilters
      );
      const completedFilterKeys = Object.keys(completedFilters);

      if (shouldUseDefaultProduct) {
        if (completedFilterKeys.length > 0) {
          const filterKey = completedFilterKeys[0];
          onSelectFilter({ [filterKey]: completedFilters[filterKey] }, false);
        }
      } else {
        onComplete(completedFilters, false);
      }
    }
  }, [
    product,
    swatches,
    onSelectSwatch,
    isLoading,
    hasSelectedFitment,
    shouldIgnoreDefaultProduct
  ]);

  useEffect(() => {
    const selectedFiltersKeys = Object.keys(selectedFilters);
    const selectedSwatchColor =
      selectedSwatch?.color || selectedSwatch?.altText;

    if (
      selectedFiltersKeys.length === 0 &&
      Array.isArray(filters[FILTER_KEYS.COLOR]) &&
      product?.code
    ) {
      if (selectedSwatch) {
        const value = selectedSwatchColor;
        onSelectFilter({ color: { label: value, value } });
      }
    } else if (
      selectedFiltersKeys.length > 0 &&
      !selectedFilters[FILTER_KEYS.COLOR] &&
      isArrayWithLength(product?.swatches)
    ) {
      if (
        product.swatches.find(
          s =>
            s.color === selectedSwatchColor || s.altText === selectedSwatchColor
        )
      ) {
        const value = selectedSwatchColor;
        onSelectFilter({ color: { label: value, value } });
      } else {
        const newSwatch = product.swatches[0];
        const value = newSwatch?.color || newSwatch?.altText;
        onSelectSwatch(newSwatch);
        onSelectFilter({ color: { label: value, value } });
      }
    }
  }, [
    selectedFilters,
    selectedSwatch,
    hasSwatches,
    filters,
    product,
    onSelectFilter,
    onSelectSwatch
  ]);

  useEffect(() => {
    onFilterChange(queryFilters);
  }, [queryFilters, onFilterChange]);

  return (
    <form styleName="container">
      <div styleName="product-swatches">
        <ProductSwatchSelectionOneDT
          availableSwatches={product?.availableColors}
          onSwatchChange={onSwatchChange}
          productCode={product?.code}
          selectedSwatch={selectedSwatch}
          showAllSwatches
          swatches={swatches}
        />
      </div>

      <InputGroup columnCount={1}>
        {filters[FILTER_KEYS.THREAD_DIAMETER]?.length > 0 &&
          filters[FILTER_KEYS.THREAD_PITCH]?.length > 0 && (
            <InputGroup columnCount={1}>
              <CanonicalFormField
                label="Thread Pitch"
                onChange={value => {
                  handleSelection(FILTER_KEYS.THREAD_PITCH, value);
                }}
                onOpenModal={() => openModal(MODAL_TYPES.THREAD_PITCH)}
                options={getOptions(FILTER_KEYS.THREAD_PITCH)}
                selectedValue={getValue(FILTER_KEYS.THREAD_PITCH)}
                showSingleOptionValues
              />

              <CanonicalFormField
                label="Thread Diameter"
                onChange={value => {
                  handleSelection(FILTER_KEYS.THREAD_DIAMETER, value);
                }}
                onOpenModal={() => openModal(MODAL_TYPES.THREAD_DIAMETER)}
                options={getOptions(FILTER_KEYS.THREAD_DIAMETER)}
                selectedValue={getValue(FILTER_KEYS.THREAD_DIAMETER)}
                showSingleOptionValues
              />

              <CanonicalFormField
                hasButtonOptions
                label="Internal Length"
                onChange={value => {
                  handleSelection(FILTER_KEYS.INTERNAL_LENGTH, value);
                }}
                onOpenModal={() => openModal(MODAL_TYPES.INTERNAL_LENGTH)}
                options={getOptions(FILTER_KEYS.INTERNAL_LENGTH)}
                selectedValue={getValue(FILTER_KEYS.INTERNAL_LENGTH)}
                showSingleOptionValues
              />

              <CanonicalFormField
                label="Seat Type"
                onChange={value => {
                  handleSelection(FILTER_KEYS.SEAT_TYPE, value);
                }}
                onOpenModal={() => openModal(MODAL_TYPES.SEAT_TYPE)}
                options={getOptions(FILTER_KEYS.SEAT_TYPE)}
                selectedValue={getValue(FILTER_KEYS.SEAT_TYPE)}
                showSingleOptionValues
              />

              <CanonicalFormField
                label="Lug Type"
                onChange={value => {
                  handleSelection(FILTER_KEYS.LUG_TYPE, value);
                }}
                onOpenModal={() => openModal(MODAL_TYPES.LUG_TYPE)}
                options={getOptions(FILTER_KEYS.LUG_TYPE)}
                selectedValue={getValue(FILTER_KEYS.LUG_TYPE)}
                showSingleOptionValues
              />
            </InputGroup>
          )}

        {filters[FILTER_KEYS.CHAIN_SECTION_WIDTH]?.length > 0 &&
          filters[FILTER_KEYS.CHAIN_ASPECT_RATIO]?.length > 0 &&
          filters[FILTER_KEYS.CHAIN_DIAMETER]?.length > 0 && (
            <div>
              <span styleName="label">Tire Size:</span>
              <InputGroup columnCount={3}>
                <CanonicalFormField
                  hideInfoButton
                  label="Width"
                  onChange={value => {
                    handleSelection(FILTER_KEYS.CHAIN_SECTION_WIDTH, value);
                  }}
                  options={getOptions(FILTER_KEYS.CHAIN_SECTION_WIDTH)}
                  selectedValue={getValue(FILTER_KEYS.CHAIN_SECTION_WIDTH)}
                  showSingleOptionValues
                />

                <CanonicalFormField
                  hideInfoButton
                  label="Ratio"
                  onChange={value => {
                    handleSelection(FILTER_KEYS.CHAIN_ASPECT_RATIO, value);
                  }}
                  options={getOptions(FILTER_KEYS.CHAIN_ASPECT_RATIO)}
                  selectedValue={getValue(FILTER_KEYS.CHAIN_ASPECT_RATIO)}
                  showSingleOptionValues
                />

                <CanonicalFormField
                  hideInfoButton
                  label="Diameter"
                  onChange={value => {
                    handleSelection(FILTER_KEYS.CHAIN_DIAMETER, value);
                  }}
                  options={getOptions(FILTER_KEYS.CHAIN_DIAMETER)}
                  selectedValue={getValue(FILTER_KEYS.CHAIN_DIAMETER)}
                  showSingleOptionValues
                />
              </InputGroup>
              <Button
                onClick={toggleHelp}
                styleName="info-button"
                type="button"
              >
                help me choose
              </Button>

              <FitmentTireCodeDescription
                className="tire-sidewall"
                isHighFloatation={false}
                isOpen={isHelpOpen}
              />
            </div>
          )}

        {filters[FILTER_KEYS.BLADE_LENGTH]?.length > 0 && (
          <div>
            <span styleName="label">Windshield Wiper Length:</span>
            <InputGroup columnCount={1}>
              <CanonicalFormField
                hideInfoButton
                hideWhatIsThisLabel
                label="Windshield Wiper Length"
                onChange={value => {
                  handleSelection(FILTER_KEYS.BLADE_LENGTH, value);
                }}
                options={getOptions(FILTER_KEYS.BLADE_LENGTH)}
                selectedValue={getValue(FILTER_KEYS.BLADE_LENGTH)}
                showSingleOptionValues
              />
            </InputGroup>
          </div>
        )}
      </InputGroup>

      {modalType === MODAL_TYPES.THREAD_DIAMETER && (
        <Modal
          heading="What Is Thread Diameter?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>THREAD DIAMETER</strong>
          <p>
            Thread diameter is the distance from side to side across the stud’s
            center.
          </p>
        </Modal>
      )}

      {modalType === MODAL_TYPES.THREAD_PITCH && (
        <Modal
          heading="What Is Thread Pitch?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>THREAD PITCH</strong>
          <p>
            Thread pitch is the distance between threads on your lug nut/stud.
          </p>
        </Modal>
      )}

      {modalType === MODAL_TYPES.INTERNAL_LENGTH && (
        <Modal
          heading="What Is Internal Length?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>INTERNAL LENGTH?</strong>
          <p>
            Internal length is the size of the lug cavity and determines thread
            engagement.
          </p>
        </Modal>
      )}

      {modalType === MODAL_TYPES.LUG_TYPE && (
        <Modal
          heading="What Is Lug Type?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>LUG TYPE?</strong>
          <p>
            Lug types include nuts or bolts and vary depending on the vehicle
            you drive.
          </p>
        </Modal>
      )}

      {modalType === MODAL_TYPES.SEAT_TYPE && (
        <Modal
          heading="What Is Seat Type?"
          isOpen={isModalOpen}
          onClose={closeModal}
        >
          <strong>SEAT TYPE?</strong>
          <p>
            Lug nut seats, (ie the lug/rim connection point), include conical,
            spherical, flat seat, mag seat, ball seat & extended thread seats.
          </p>
        </Modal>
      )}
    </form>
  );
}

CanonicalAccessoryForm.propTypes = {
  isLoading: PropTypes.bool,
  onFilterChange: PropTypes.func,
  onSelectSwatch: PropTypes.func,
  product: ProductProp,
  selectedSwatch: PropTypes.object,
  swatches: PropTypes.func
};

export default CanonicalAccessoryForm;
